import * as React from 'react';
import { formatPattern } from 'react-router';

import useRouter from 'js/lib/useRouter';

import type { IconComponent } from 'bundles/enterprise-admin-navigation/components/side-nav/OrgSwitcher/types';
import {
  type OrgSwitcherItemConfig,
  type OrgSwitcherRuntimeContext,
  orgSwitcherConfigDefs,
} from 'bundles/enterprise-admin-navigation/components/side-nav/config/orgSwitcherConfigDefs';
import { computeSearchCarry, isExternalHref } from 'bundles/enterprise-admin-navigation/utils/navConfigUtils';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import {
  ENTERPRISE_ADMIN_PERMISSION_SUBDOMAIN as SUB,
  ENTERPRISE_ADMIN_PERMISSION_VERB as VERB,
} from 'bundles/enterprise-admin-roles/types';
import { isSelfServiceSsoEnabled } from 'bundles/enterprise-admin-sso-settings/SelfServiceSsoExperiment';
import enterpriseAdminUser from 'bundles/enterprise-admin/enterprise-admin-index';
import { useProductFeatures } from 'bundles/enterprise-admin/features';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';

export type ResolvedOrgSwitcherItem = {
  id: string;
  label: string;
  icon: IconComponent;
  href: string;
  external: boolean;
};

/**
 * Walks a single `OrgSwitcherItemConfig` through the runtime environment.
 *
 * Applies visibility gates (shouldShow, requiredPermissions) and resolves hrefs.
 * Returns null if the item should be dropped (failed gate or permission check).
 */
const resolveItem = (
  item: OrgSwitcherItemConfig,
  runtimeContext: OrgSwitcherRuntimeContext,
  routerParams: Record<string, string | undefined>,
  locationSearchString: string
): ResolvedOrgSwitcherItem | null => {
  if (item.shouldShow && !item.shouldShow(runtimeContext)) return null;
  if (item.requiredPermissions && !runtimeContext.hasPermission(item.requiredPermissions)) return null;

  const isExternal = isExternalHref(item.href);
  // Format route template with org slug parameter for internal routes
  const formatted = isExternal ? item.href : formatPattern(item.href, routerParams);
  const href = isExternal ? formatted : `${formatted}${locationSearchString}`;

  return {
    id: item.id,
    label: item.getLabel(),
    icon: item.icon,
    href,
    external: item.external ?? false,
  };
};

export type AdminOrgSwitcherItems = {
  settings: ResolvedOrgSwitcherItem[];
  help: ResolvedOrgSwitcherItem[];
};

type UseAdminOrgSwitcherItemsArgs = {
  thirdPartyOrganization: AdminDashboardOrg;
  enterpriseAdminFeatureFlags: {
    enableNewLearnersPage: boolean;
  };
};

/**
 * Resolves the declarative popup config into ready-to-render items, grouped by
 * section. The consumer renders dividers between sections and appends any
 * action items (e.g. Logout) directly.
 */
export const useAdminOrgSwitcherItems = ({
  thirdPartyOrganization,
  enterpriseAdminFeatureFlags,
}: UseAdminOrgSwitcherItemsArgs): AdminOrgSwitcherItems => {
  const router = useRouter();
  const { hasPermission, getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const { enableNewLearnersPage } = enterpriseAdminFeatureFlags;
  const { features } = useProductFeatures({ idType: 'Organization', id: thirdPartyOrganization.id });

  const enableSelfServiceSso = features.get('enterprise', 'enableSelfServiceSso') && isSelfServiceSsoEnabled();
  const isDirectSale = enterpriseAdminUser.isStandardContractUser();
  const isSelfServe = !isDirectSale;
  const enableWesBilling =
    isSelfServe &&
    hasPermission({
      domain: 'ORGANIZATION',
      domainId: thirdPartyOrganization.id,
      subdomain: SUB.SELF_SERVE_CONTRACTS,
      verb: VERB.MANAGE,
    });

  const locationSearchString = React.useMemo(
    () => computeSearchCarry(router.location.search || ''),
    [router.location.search]
  );

  return React.useMemo(() => {
    const runtimeContext: OrgSwitcherRuntimeContext = {
      thirdPartyOrganization,
      hasPermission,
      isOrganizationDomainAdmin: getIsOrganizationDomainAdmin(),
      enableNewLearnersPage,
      enableSelfServiceSso,
      enableWesBilling,
      isDirectSale,
    };

    const grouped: AdminOrgSwitcherItems = { settings: [], help: [] };
    orgSwitcherConfigDefs.forEach((item) => {
      const resolved = resolveItem(item, runtimeContext, router.params, locationSearchString);
      if (!resolved) return;
      grouped[item.section].push(resolved);
    });
    return grouped;
  }, [
    thirdPartyOrganization,
    router.params,
    hasPermission,
    getIsOrganizationDomainAdmin,
    enableNewLearnersPage,
    enableSelfServiceSso,
    enableWesBilling,
    isDirectSale,
    locationSearchString,
  ]);
};
